import {
  Button,
  FormControl,
  IconButton,
  Input,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CreateUpdateQuotationItemFlightInputWRowId,
  QuotationItemFlightsProps,
} from "./interface";
import TableListTitle from "../TableList/TableListTitle";
import update from "immutability-helper";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { DeleteIcon } from "@chakra-ui/icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  Row,
} from "@tanstack/react-table";
import { CreateUpdateQuotationItemFlightInput } from "../../generated/graphql";
import { v4 as uuidv4 } from "uuid";

const columnHelper = createColumnHelper<CreateUpdateQuotationItemFlightInput>();

const FieldFlights: React.FC<QuotationItemFlightsProps> = ({
  value,
  onChange,
  isError,
}) => {
  const [items, setItems] = useState<
    CreateUpdateQuotationItemFlightInputWRowId[]
  >([]);

  useEffect(() => {
    setItems(value?.map((elm) => ({ ...elm, rowId: uuidv4() })) || []);
  }, [value]);

  const addItem = () => {
    const newItems = update(items, {
      $push: [
        {
          rowId: uuidv4(),
          flightNumber: "",
          travel: "",
          departTime: "",
          arrivalTime: "",
        },
      ],
    });

    setItems(newItems);
    onChange(newItems.map(({ rowId, ...elm }) => elm));
  };

  const { confirmation, close } = useConfirmation();

  const onInputChange = useCallback(
    (
      index: number,
      flightNumber: string,
      travel: string,
      departTime: string,
      arrivalTime: string
    ) => {
      const newItems = update(items, {
        [index]: {
          flightNumber: {
            $set: flightNumber,
          },
          travel: {
            $set: travel,
          },
          departTime: {
            $set: departTime,
          },
          arrivalTime: {
            $set: arrivalTime,
          },
        },
      });

      setItems(newItems);
      onChange(newItems.map(({ rowId, ...elm }) => elm));
    },
    [items, onChange]
  );

  const columns = useMemo(() => {
    const deleteRow = (index: number) => {
      confirmation({
        description: "Are you sure want to delete the data?",
        callback: async () => {
          const newItems = update(items, {
            $splice: [[index, 1]],
          });

          setItems(newItems);
          onChange(newItems.map(({ rowId, ...elm }) => elm));
          close();
        },
      });
    };

    return [
      columnHelper.accessor("flightNumber", {
        id: "flightNumber",
        header: () => "Flight Number",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("travel", {
        id: "travel",
        header: () => "Travel",
      }),
      columnHelper.accessor("departTime", {
        id: "departTime",
        header: () => "Depart Time",
      }),
      columnHelper.accessor("arrivalTime", {
        id: "arrivalTime",
        header: () => "Arrival Time",
      }),
      columnHelper.display({
        id: "action",
        header: "",
        cell: ({ row }) => (
          <IconButton
            aria-label="Hapus"
            variant="icon"
            size="sm"
            icon={<DeleteIcon />}
            onClick={() => deleteRow(row.index)}
          />
        ),
      }),
    ];
  }, [close, confirmation, items, onChange]);

  const table = useReactTable({
    data: items,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: false,
    getRowCanExpand: () => true,
  });

  return (
    <>
      <TableListTitle title="Flights" />

      <Table>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        {table.getRowModel().rows.length >= 1 && (
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <RowRender
                key={row.id}
                row={row}
                onChange={(flightNumber, travel, departTime, arrivalTime) =>
                  onInputChange(
                    row.index,
                    flightNumber,
                    travel,
                    departTime,
                    arrivalTime
                  )
                }
                isError={isError}
              />
            ))}
          </Tbody>
        )}
        {table.getRowModel().rows.length <= 0 && (
          <Tbody>
            <Tr>
              <Td colSpan={4} textAlign="center">
                There is no data!
              </Td>
            </Tr>
          </Tbody>
        )}
      </Table>

      <Button size="sm" onClick={() => addItem()} mt={4}>
        Add Flight
      </Button>
    </>
  );
};

const RowRender = ({
  row,
  onChange,
  isError,
}: {
  row: Row<CreateUpdateQuotationItemFlightInput>;
  onChange: (
    flightNumber: string,
    travel: string,
    departTime: string,
    arrivalTime: string
  ) => void;
  isError?: boolean;
}) => {
  const [flightNumber, setFlightNumber] = useState<string>("");
  const [travel, setTravel] = useState<string>("");
  const [departTime, setDepartTime] = useState<string>("");
  const [arrivalTime, setArrivalTime] = useState<string>("");

  useEffect(() => {
    setFlightNumber(row.getValue("flightNumber"));
    setTravel(row.getValue("travel"));
    setDepartTime(row.getValue("departTime"));
    setArrivalTime(row.getValue("arrivalTime"));
  }, [row]);

  return (
    <Tr>
      {row.getVisibleCells().map((cell) => {
        return (
          <Td key={cell.id}>
            {cell.column.columnDef.id === "flightNumber" ? (
              <FormControl isInvalid={isError && !flightNumber} mb={0}>
                <Input
                  size="sm"
                  onChange={(e: any) => {
                    setFlightNumber(e.target.value);
                    onChange(e.target.value, travel, departTime, arrivalTime);
                  }}
                  value={flightNumber.toString()}
                />
              </FormControl>
            ) : cell.column.columnDef.id === "travel" ? (
              <FormControl isInvalid={isError && !travel} mb={0}>
                <Input
                  size="sm"
                  onChange={(e: any) => {
                    setTravel(e.target.value);
                    onChange(
                      flightNumber,
                      e.target.value,
                      departTime,
                      arrivalTime
                    );
                  }}
                  value={travel.toString()}
                />
              </FormControl>
            ) : cell.column.columnDef.id === "departTime" ? (
              <FormControl isInvalid={isError && !departTime} mb={0}>
                <Input
                  size="sm"
                  type="datetime-local"
                  onChange={(e: any) => {
                    setDepartTime(e.target.value);
                    onChange(flightNumber, travel, e.target.value, arrivalTime);
                  }}
                  value={departTime.toString()}
                  w="200px"
                />
              </FormControl>
            ) : cell.column.columnDef.id === "arrivalTime" ? (
              <FormControl isInvalid={isError && !arrivalTime} mb={0}>
                <Input
                  size="sm"
                  type="datetime-local"
                  onChange={(e: any) => {
                    setArrivalTime(e.target.value);
                    onChange(flightNumber, travel, departTime, e.target.value);
                  }}
                  value={arrivalTime.toString()}
                  w="200px"
                />
              </FormControl>
            ) : (
              <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>
            )}
          </Td>
        );
      })}
    </Tr>
  );
};

export default FieldFlights;
